import { z } from 'zod';
import {
  RESULTATS_INTERACTION,
  ROLES,
  SOURCES_DIRIGEANT,
  SOURCES_SYNC,
  STATUTS_ENRICHISSEMENT,
  STATUTS_SIRENE,
  STATUTS_SYNC,
  TYPES_INTERACTION,
  TYPES_PROCHAINE_ETAPE,
  type OrigineLead,
  type Role,
  type TypeProchaineEtape,
} from './domain.js';
import { siretValide } from './siret.js';

/* -------------------------------------------------------------------------- */
/*                                  Primitifs                                  */
/* -------------------------------------------------------------------------- */

export const sirenSchema = z
  .string()
  .regex(/^\d{9}$/, 'Le SIREN doit contenir exactement 9 chiffres');

export const siretSchema = z
  .string()
  .regex(/^\d{14}$/, 'Le SIRET doit contenir exactement 14 chiffres');

/**
 * SIRET saisi par un utilisateur : espaces toleres, cle de controle verifiee
 * (conception 5.2). `siretSchema` reste un simple controle de format ; un
 * format faux ne produit que son propre message.
 */
export const siretControleSchema = z.preprocess(
  (valeur) => (typeof valeur === 'string' ? valeur.replace(/\s+/g, '') : valeur),
  siretSchema.refine(
    (siret) => !/^\d{14}$/.test(siret) || siretValide(siret),
    'SIRET invalide : la clé de contrôle ne correspond pas',
  ),
);

/**
 * Codes departements valides. Le code "20" est volontairement exclu : la Corse
 * se decline en 2A et 2B, et 96 / 99 n'existent pas.
 */
export const departementSchema = z
  .string()
  .regex(/^(?:0[1-9]|1\d|2[AB]|2[1-9]|[3-8]\d|9[0-5]|97[1-6])$/, 'Code département invalide');

export const codePostalSchema = z.string().regex(/^\d{5}$/, 'Code postal invalide');

/** Date ISO (jour ou date-heure) acceptee en entree d'API. */
export const dateIsoSchema = z
  .string()
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Date ISO invalide');

/* -------------------------------------------------------------------------- */
/*                              Pagination et tri                              */
/* -------------------------------------------------------------------------- */

export const CHAMPS_TRI_LEAD = [
  'denomination',
  'dateCreation',
  'ville',
  'departement',
  'noteGoogle',
  'nbAvisGoogle',
  'effectif',
  'statutCommercial',
  'dateProchaineAction',
  'majLe',
] as const;
export type ChampTriLead = (typeof CHAMPS_TRI_LEAD)[number];

/**
 * Booleen tolerant aux query strings : "true"/"1"/"oui" -> true,
 * "false"/"0"/"non"/"" -> false. `z.coerce.boolean()` ne convient pas ici
 * car il transforme la chaine "false" en `true`.
 */
export const booleenQuerySchema = z.preprocess((v) => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const n = v.trim().toLowerCase();
    if (['true', '1', 'oui', 'yes', 'on'].includes(n)) return true;
    if (['false', '0', 'non', 'no', 'off', ''].includes(n)) return false;
  }
  return v;
}, z.boolean());

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  taillePage: z.coerce.number().int().min(1).max(200).default(25),
});

export interface ReponsePaginee<T> {
  donnees: T[];
  pagination: {
    page: number;
    taillePage: number;
    total: number;
    nbPages: number;
  };
}

/* -------------------------------------------------------------------------- */
/*                            Filtres de recherche                             */
/* -------------------------------------------------------------------------- */

/**
 * Filtres de la vue liste et de la vue Kanban (cahier des charges 3.3).
 * Tous les champs sont optionnels et combinables.
 */
export const filtresLeadSchema = z.object({
  /** Recherche plein texte sur denomination, enseigne, ville, SIREN, SIRET. */
  q: z.string().trim().min(1).max(120).optional(),
  departement: z.union([departementSchema, z.array(departementSchema)]).optional(),
  ville: z.string().trim().min(1).max(120).optional(),
  codePostal: codePostalSchema.optional(),
  statutCommercial: z.union([z.string(), z.array(z.string())]).optional(),
  statutEnrichissement: z.enum(STATUTS_ENRICHISSEMENT).optional(),
  statutSirene: z.enum(STATUTS_SIRENE).optional(),
  secteur: z.string().optional(),
  /**
   * Nature juridique de l'exploitant. `PHYSIQUE` isole les entrepreneurs
   * individuels et professions liberales (categorie juridique 1000),
   * `MORALE` ne garde que les societes.
   */
  typePersonne: z.enum(['PHYSIQUE', 'MORALE']).optional(),
  /**
   * Nature d'activite declaree au RNE. Renseignee par l'enrichissement INPI :
   * les leads jamais enrichis n'en ont pas et sont exclus par ce filtre.
   */
  natureActivite: z.union([z.string(), z.array(z.string())]).optional(),
  /** Ecarte les activites liberales, reglementees ou non. */
  exclureLiberales: booleenQuerySchema.optional(),
  codeNaf: z.string().optional(),
  /** Borne basse de la tranche d'effectif SIRENE. */
  effectifMin: z.coerce.number().int().min(0).optional(),
  effectifMax: z.coerce.number().int().min(0).optional(),
  /** Anciennete de l'etablissement en annees, calculee depuis dateCreation. */
  ancienneteMinAnnees: z.coerce.number().int().min(0).max(150).optional(),
  ancienneteMaxAnnees: z.coerce.number().int().min(0).max(150).optional(),
  /** true = uniquement ceux qui ont un site web, false = uniquement ceux sans. */
  avecSiteWeb: booleenQuerySchema.optional(),
  avecTelephone: booleenQuerySchema.optional(),
  avecDirigeant: booleenQuerySchema.optional(),
  noteMin: z.coerce.number().min(0).max(5).optional(),
  noteMax: z.coerce.number().min(0).max(5).optional(),
  nbAvisMin: z.coerce.number().int().min(0).optional(),
  /** Leads dont la prochaine action est due avant cette date (relances). */
  relanceAvant: dateIsoSchema.optional(),
  relanceApres: dateIsoSchema.optional(),
  /** true = leads du vivier : aucun commercial, ligne de suivi absente comprise. */
  sansProprietaire: booleenQuerySchema.optional(),
  /** true = aucune date de prochaine etape, ligne de suivi absente comprise. */
  sansProchaineEtape: booleenQuerySchema.optional(),
  /** Par defaut les leads en opposition sont exclus des listes. */
  inclureNePlusContacter: booleenQuerySchema.default(false),
  proprietaireId: z.string().uuid().optional(),
});

export type FiltresLead = z.infer<typeof filtresLeadSchema>;

export const requeteListeLeadsSchema = filtresLeadSchema.merge(paginationSchema).extend({
  tri: z.enum(CHAMPS_TRI_LEAD).default('majLe'),
  ordre: z.enum(['asc', 'desc']).default('desc'),
});
export type RequeteListeLeads = z.infer<typeof requeteListeLeadsSchema>;

/* -------------------------------------------------------------------------- */
/*                              Ressources lues                                */
/* -------------------------------------------------------------------------- */

/** Compte reduit a ce qu'en affiche une fiche : proprietaire, createur. */
export interface UtilisateurResumeDTO {
  id: string;
  nom: string;
}

export interface DirigeantDTO {
  id: string;
  nom: string;
  prenom: string | null;
  nomComplet: string;
  fonction: string | null;
  source: (typeof SOURCES_DIRIGEANT)[number];
  actif: boolean;
  /** URL de recherche Google/LinkedIn pre-remplie (cahier des charges 2.4). */
  urlRechercheLinkedin: string;
}

export interface GooglePlaceDTO {
  placeId: string;
  note: number | null;
  nbAvis: number | null;
  telephone: string | null;
  siteWeb: string | null;
  adresseFormatee: string | null;
  businessStatus: string | null;
  horaires: string[] | null;
  latitude: number | null;
  longitude: number | null;
  /** Score de similarite du rapprochement SIRENE <-> Google (0 a 1). */
  scoreMatching: number;
  urlFiche: string | null;
  syncLe: string | null;
}

export interface SuiviCommercialDTO {
  statutCode: string;
  statutLibelle: string;
  statutCouleur: string;
  prochaineAction: string | null;
  dateProchaineAction: string | null;
  /** Type de la prochaine etape ; `null` si elle n'est pas typee. */
  typeProchaineAction: TypeProchaineEtape | null;
  priorite: number;
  proprietaireId: string | null;
  proprietaireNom: string | null;
  majLe: string;
}

export interface InteractionDTO {
  id: string;
  leadId: string;
  type: (typeof TYPES_INTERACTION)[number];
  date: string;
  notes: string | null;
  resultat: (typeof RESULTATS_INTERACTION)[number] | null;
  dureeSecondes: number | null;
  auteurId: string | null;
  auteurNom: string | null;
  creeLe: string;
}

export interface LeadResumeDTO {
  id: string;
  siren: string;
  siret: string;
  denomination: string;
  enseigne: string | null;
  ville: string | null;
  codePostal: string | null;
  departement: string | null;
  dateCreation: string | null;
  trancheEffectif: string | null;
  trancheEffectifLibelle: string;
  statutSirene: (typeof STATUTS_SIRENE)[number];
  statutEnrichissement: (typeof STATUTS_ENRICHISSEMENT)[number];
  nePlusContacter: boolean;
  dirigeantPrincipal: string | null;
  telephone: string | null;
  siteWeb: string | null;
  noteGoogle: number | null;
  nbAvisGoogle: number | null;
  suivi: SuiviCommercialDTO | null;
  /** Proprietaire du lead ; `null` pour un lead du vivier. */
  proprietaire: UtilisateurResumeDTO | null;
  majLe: string;
}

export interface LeadDetailDTO extends LeadResumeDTO {
  adresseLigne1: string | null;
  adresseLigne2: string | null;
  adresseComplete: string;
  region: string | null;
  latitude: number | null;
  longitude: number | null;
  codeNaf: string | null;
  libelleNaf: string | null;
  secteur: string | null;
  formeJuridique: string | null;
  natureActivite: string | null;
  dateFermeture: string | null;
  nePlusContacterLe: string | null;
  nePlusContacterMotif: string | null;
  notesLibres: string | null;
  /** E-mail saisi a la main (aucune source externe n'en fournit). */
  email: string | null;
  origine: OrigineLead;
  /** Auteur d'une creation manuelle ; `null` pour un import. */
  creePar: UtilisateurResumeDTO | null;
  sireneSyncLe: string | null;
  inpiSyncLe: string | null;
  placesSyncLe: string | null;
  motifEnrichissement: string | null;
  dirigeants: DirigeantDTO[];
  googlePlace: GooglePlaceDTO | null;
  interactions: InteractionDTO[];
  /** Recherche LinkedIn au niveau de l'etablissement (sans dirigeant connu). */
  urlRechercheLinkedin: string;
  creeLe: string;
}

/* -------------------------------------------------------------------------- */
/*                              Ecritures                                      */
/* -------------------------------------------------------------------------- */

export const majLeadSchema = z.object({
  notesLibres: z.string().max(20000).nullish(),
  telephoneManuel: z.string().max(40).nullish(),
  siteWebManuel: z.string().max(500).nullish(),
  emailManuel: z.string().email().max(320).nullish(),
});
export type MajLead = z.infer<typeof majLeadSchema>;

/** Droit d'opposition RGPD (cahier des charges 5). */
export const oppositionSchema = z.object({
  nePlusContacter: z.boolean(),
  motif: z.string().max(500).optional(),
});
export type Opposition = z.infer<typeof oppositionSchema>;

/**
 * Suppression definitive de plusieurs leads.
 *
 * Les fiches en opposition sont ecartees par defaut : les supprimer efface la
 * trace du refus, et la synchronisation SIRENE suivante reimporte
 * l'etablissement comme un prospect neuf, a recontacter.
 */
export const suppressionLeadsSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
  /** Supprime aussi les fiches en opposition, en connaissance de cause. */
  inclureOpposants: z.boolean().default(false),
});
export type SuppressionLeads = z.infer<typeof suppressionLeadsSchema>;

export interface ResultatSuppressionDTO {
  supprimes: number;
  /** Fiches ecartees car en opposition RGPD. */
  ignoresOpposition: number;
  /** Identifiants demandes qui n'existaient plus. */
  introuvables: number;
}

/** Texte facultatif d'un formulaire : rogne, et lit une chaine vide comme une absence. */
function texteFacultatif(longueurMax: number) {
  return z
    .string()
    .trim()
    .max(longueurMax)
    .transform((valeur) => (valeur === '' ? null : valeur))
    .nullish();
}

/** Prochaine etape (conception 7.1) : type, date et note s'ecrivent et s'effacent ensemble. */
export const prochaineEtapeSchema = z.object({
  type: z.enum(TYPES_PROCHAINE_ETAPE),
  date: dateIsoSchema,
  note: texteFacultatif(500),
});
export type ProchaineEtape = z.infer<typeof prochaineEtapeSchema>;

export const majSuiviSchema = z.object({
  statutCode: z.string().min(1).max(60).optional(),
  prochaineAction: z.string().max(500).nullish(),
  dateProchaineAction: dateIsoSchema.nullish(),
  /** Type de la prochaine etape ; une note ne se planifie pas. */
  typeProchaineAction: z.enum(TYPES_PROCHAINE_ETAPE).nullish(),
  priorite: z.number().int().min(0).max(5).optional(),
  proprietaireId: z.string().uuid().nullish(),
});
export type MajSuivi = z.infer<typeof majSuiviSchema>;

export const creerInteractionSchema = z.object({
  type: z.enum(TYPES_INTERACTION),
  date: dateIsoSchema.optional(),
  notes: z.string().max(20000).nullish(),
  resultat: z.enum(RESULTATS_INTERACTION).nullish(),
  dureeSecondes: z.number().int().min(0).max(86400).nullish(),
  /** Programme la prochaine action dans la foulee de la saisie. */
  prochaineAction: z.string().max(500).nullish(),
  dateProchaineAction: dateIsoSchema.nullish(),
  /** Prochaine etape typee, meme transaction ; `null` l'efface. Fournie, elle prime sur les deux champs ci-dessus. */
  prochaineEtape: prochaineEtapeSchema.nullable().optional(),
  /** Nouvelle etape du lead, changee dans la meme transaction. */
  statutCode: z.string().min(1).max(60).optional(),
});
export type CreerInteraction = z.infer<typeof creerInteractionSchema>;

export const creerDirigeantSchema = z.object({
  nom: z.string().min(1).max(120),
  prenom: z.string().max(120).nullish(),
  fonction: z.string().max(160).nullish(),
  source: z.enum(SOURCES_DIRIGEANT).default('MANUEL'),
});
export type CreerDirigeant = z.infer<typeof creerDirigeantSchema>;

export const majStatutCommercialSchema = z.object({
  code: z.string().min(1).max(60).regex(/^[A-Z0-9_]+$/, 'Code en majuscules, chiffres et underscores'),
  libelle: z.string().min(1).max(80),
  couleur: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Couleur hexadécimale attendue'),
  ordre: z.number().int().min(0).max(1000),
  estInitial: z.boolean().default(false),
  estFinal: z.boolean().default(false),
  estGagne: z.boolean().default(false),
});
export type MajStatutCommercial = z.infer<typeof majStatutCommercialSchema>;

export interface StatutCommercialDTO extends MajStatutCommercial {
  id: string;
  nbLeads?: number;
}

/* -------------------------------------------------------------------------- */
/*          Creation manuelle, recherche SIRENE et actions groupees            */
/* -------------------------------------------------------------------------- */

/** E-mail facultatif : une saisie vide vaut absence. */
const emailFacultatif = z.preprocess(
  (valeur) => (typeof valeur === 'string' && valeur.trim() === '' ? null : valeur),
  z.string().trim().email('Adresse e-mail invalide').max(320).nullish(),
);

/** Creation manuelle (`POST /leads`) : seul le SIRET est requis, SIRENE fournit le reste. */
export const creerLeadSchema = z.object({
  siret: siretControleSchema,
  telephone: texteFacultatif(40),
  email: emailFacultatif,
  siteWeb: texteFacultatif(500),
  /** Contact principal, enregistre comme dirigeant de source MANUEL. */
  contact: z
    .object({
      nom: z.string().trim().min(1, 'Le nom du contact est obligatoire').max(120),
      prenom: texteFacultatif(120),
      fonction: texteFacultatif(160),
    })
    .nullish(),
  /** Omis : le createur. Un autre compte, ou `null` (vivier), exige `leads.attribuer`. */
  proprietaireId: z.string().uuid().nullish(),
  prochaineEtape: prochaineEtapeSchema.nullish(),
  notes: texteFacultatif(20000),
});
export type CreerLead = z.infer<typeof creerLeadSchema>;

/** Recherche SIRENE avant une creation (`GET /entreprises/recherche`). */
export const rechercheEntreprisesSchema = z.object({
  /** Nom, SIREN ou SIRET. */
  q: z.string().trim().min(3, 'Saisissez au moins 3 caractères').max(120),
  codePostal: z.preprocess((valeur) => (valeur === '' ? undefined : valeur), codePostalSchema.optional()),
});
export type RechercheEntreprises = z.infer<typeof rechercheEntreprisesSchema>;

/** Un etablissement du repertoire SIRENE, propose avant une creation manuelle. */
export interface EtablissementTrouveDTO {
  siret: string;
  siren: string;
  denomination: string;
  enseigne: string | null;
  /** Numero, voie et complement. */
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  /** `false` : etablissement ferme. */
  actif: boolean;
  /** `AAAA-MM-JJ`. */
  dateCreation: string | null;
  /** Lead portant deja ce SIRET ; `visible` : dans le perimetre de l'appelant (sinon, il ne s'ouvre pas). */
  dejaEnBase: { leadId: string; visible: boolean } | null;
}

/** Identifiants d'une action groupee : 500 au plus, comme la suppression multiple. */
const idsLotSchema = z.array(z.string().uuid()).min(1).max(500);

export const attribuerLotSchema = z.object({
  ids: idsLotSchema,
  /** Nouveau proprietaire ; `null` rend les leads au vivier. */
  proprietaireId: z.string().uuid().nullable(),
});
export type AttribuerLot = z.infer<typeof attribuerLotSchema>;

export const etapeLotSchema = z.object({
  ids: idsLotSchema,
  statutCode: z.string().min(1).max(60),
});
export type EtapeLot = z.infer<typeof etapeLotSchema>;

export interface ResultatLotDTO {
  /** Leads effectivement modifies. */
  modifies: number;
  /** Leads laisses tels quels : inconnus, hors perimetre ou deja dans l'etat demande. */
  ignores: number;
}

/* -------------------------------------------------------------------------- */
/*                          Import / enrichissement                            */
/* -------------------------------------------------------------------------- */

export const importSireneSchema = z
  .object({
    codeNaf: z.string().default('85.53Z'),
    departements: z.array(departementSchema).max(101).optional(),
    communes: z.array(codePostalSchema).max(200).optional(),
    /** Ne recupere que les etablissements crees apres cette date. */
    creesApres: dateIsoSchema.optional(),
    /** Uniquement les etablissements actifs (defaut true). */
    uniquementActifs: z.boolean().default(true),
    /**
     * Ecarte les entreprises individuelles et professions liberales.
     * La categorie juridique 1000 designe toute personne physique, quelle que
     * soit son activite : c'est le levier pour ne garder que des societes.
     */
    exclurePersonnesPhysiques: z.boolean().default(false),
    /** Ne garde que les etablissements declarant au moins un salarie. */
    uniquementEmployeurs: z.boolean().default(false),
    /**
     * Nombre de leads a obtenir, et non d'etablissements a examiner.
     *
     * La recherche se poursuit tant que l'objectif n'est pas atteint : avec des
     * criteres de qualite, il faut souvent examiner bien plus d'etablissements
     * que de leads finalement gardes. Un plafond interne borne malgre tout le
     * nombre d'examens, pour ne pas derouler tout le stock SIRENE.
     */
    limite: z.number().int().min(1).max(50000).default(5000),
    /** Declenche l'enrichissement INPI + Places a la suite de l'import. */
    enrichirApres: z.boolean().default(true),
    /**
     * N'ajoute un nouvel etablissement que si une fiche Google lui est
     * rapprochee. Un moniteur independant sans local n'en a generalement pas.
     */
    exigerFicheGoogle: z.boolean().default(false),
    /** N'ajoute un nouvel etablissement que si un dirigeant est publie au RNE. */
    exigerDirigeant: z.boolean().default(false),
  })
  .refine(
    (v) => (v.departements?.length ?? 0) > 0 || (v.communes?.length ?? 0) > 0 || v.creesApres != null,
    { message: 'Précisez au moins un département, une commune ou une date de création' },
  );
export type ImportSirene = z.infer<typeof importSireneSchema>;

export const enrichirLeadSchema = z.object({
  sources: z.array(z.enum(SOURCES_SYNC)).min(1).default(['INPI', 'GOOGLE_PLACES']),
  forcer: z.boolean().default(false),
});
export type EnrichirLead = z.infer<typeof enrichirLeadSchema>;

export interface TacheDTO {
  id: string;
  file: string;
  nom: string;
  etat: 'en_attente' | 'actif' | 'termine' | 'echec' | 'retarde' | 'inconnu';
  progression: number;
  resultat: unknown;
  erreur: string | null;
  creeLe: string | null;
  termineLe: string | null;
}

export interface ExecutionSyncDTO {
  id: string;
  source: (typeof SOURCES_SYNC)[number];
  statut: (typeof STATUTS_SYNC)[number];
  demarreLe: string;
  termineLe: string | null;
  nbTraites: number;
  nbCrees: number;
  nbMisAJour: number;
  nbIgnores: number;
  nbErreurs: number;
  message: string | null;
  parametres: Record<string, unknown> | null;
}

/* -------------------------------------------------------------------------- */
/*                                Tableau de bord                              */
/* -------------------------------------------------------------------------- */

/**
 * Filtre des statistiques. `proprietaireId` (un commercial) est reserve a
 * `stats.equipe` ; sans cette permission, on ne voit que ses propres leads.
 */
export const requeteStatsSchema = z.object({
  proprietaireId: z.string().uuid().optional(),
});
export type RequeteStats = z.infer<typeof requeteStatsSchema>;

export interface StatsDashboardDTO {
  totalLeads: number;
  leadsActifs: number;
  nePlusContacter: number;
  parStatut: Array<{
    code: string;
    libelle: string;
    couleur: string;
    ordre: number;
    nb: number;
  }>;
  parEnrichissement: Array<{ statut: (typeof STATUTS_ENRICHISSEMENT)[number]; nb: number }>;
  tauxConversion: number;
  tauxConversionDetail: { convertis: number; travailles: number };
  relances: {
    enRetard: number;
    aujourdhui: number;
    cetteSemaine: number;
  };
  qualite: {
    avecSiteWeb: number;
    sansSiteWeb: number;
    avecTelephone: number;
    avecDirigeant: number;
    noteMoyenne: number | null;
  };
  derniereSyncSirene: string | null;
}

export interface RepartitionGeoDTO {
  departements: Array<{
    code: string;
    nom: string;
    nb: number;
    convertis: number;
  }>;
  total: number;
}

export interface RelanceDTO {
  leadId: string;
  denomination: string;
  ville: string | null;
  departement: string | null;
  telephone: string | null;
  statutCode: string;
  statutLibelle: string;
  prochaineAction: string | null;
  dateProchaineAction: string;
  enRetard: boolean;
}

/* -------------------------------------------------------------------------- */
/*                                    Export                                   */
/* -------------------------------------------------------------------------- */

export const FORMATS_EXPORT = ['csv', 'xlsx', 'emailing'] as const;
export type FormatExport = (typeof FORMATS_EXPORT)[number];

/**
 * Export (cahier des charges 3.5). Les opposants en sont toujours exclus
 * (conception 5.2) : `inclureNePlusContacter` n'existe pas ici.
 */
export const requeteExportSchema = filtresLeadSchema.omit({ inclureNePlusContacter: true }).extend({
  format: z.enum(FORMATS_EXPORT).default('csv'),
  /** Colonnes a exporter ; toutes par defaut. */
  colonnes: z.array(z.string()).optional(),
});
export type RequeteExport = z.infer<typeof requeteExportSchema>;

/* -------------------------------------------------------------------------- */
/*                              Authentification                               */
/* -------------------------------------------------------------------------- */

export const connexionSchema = z.object({
  email: z.string().email(),
  motDePasse: z.string().min(8).max(200),
});
export type Connexion = z.infer<typeof connexionSchema>;

/**
 * Mot de passe choisi par son titulaire (conception 3.3) : 10 caracteres au
 * moins. La connexion garde son ancien minimum : un compte existant ne doit pas
 * se retrouver verrouille par le durcissement de la regle.
 */
export const motDePasseSchema = z
  .string()
  .min(10, 'Le mot de passe doit contenir au moins 10 caractères')
  .max(200, 'Le mot de passe ne peut pas dépasser 200 caractères');

/** Changement de mot de passe par son titulaire (`POST /auth/mot-de-passe`). */
export const changerMotDePasseSchema = z
  .object({
    motDePasseActuel: z.string().min(1, 'Saisissez votre mot de passe actuel').max(200),
    nouveauMotDePasse: motDePasseSchema,
  })
  .refine((corps) => corps.nouveauMotDePasse !== corps.motDePasseActuel, {
    message: "Le nouveau mot de passe doit être différent de l'actuel",
    path: ['nouveauMotDePasse'],
  });
export type ChangerMotDePasse = z.infer<typeof changerMotDePasseSchema>;

/** Ce que le titulaire modifie lui-meme sur son profil (`PATCH /auth/moi`). */
export const majProfilSchema = z.object({
  nom: z.string().trim().min(1, 'Le nom est obligatoire').max(120),
});
export type MajProfil = z.infer<typeof majProfilSchema>;

export interface UtilisateurDTO {
  id: string;
  email: string;
  nom: string;
  role: (typeof ROLES)[number];
  /**
   * Mot de passe provisoire : tant que vrai, seules GET/PATCH /auth/moi et
   * POST /auth/mot-de-passe repondent, l'interface impose le changement.
   */
  doitChangerMotDePasse: boolean;
}

export interface ReponseConnexionDTO {
  token: string;
  utilisateur: UtilisateurDTO;
  expireDans: number;
}

/* -------------------------------------------------------------------------- */
/*                                    Equipe                                   */
/* -------------------------------------------------------------------------- */

/** Creation d'un compte par un administrateur (`POST /utilisateurs`). */
export const creerUtilisateurSchema = z.object({
  // Normalise ici : l'unicite de l'e-mail en base est sensible a la casse.
  email: z.string().trim().toLowerCase().email('Adresse e-mail invalide').max(320),
  nom: z.string().trim().min(1, 'Le nom est obligatoire').max(120),
  role: z.enum(ROLES),
});
export type CreerUtilisateur = z.infer<typeof creerUtilisateurSchema>;

/** Modification d'un compte (`PATCH /utilisateurs/:id`) : au moins un champ. */
export const majUtilisateurSchema = z
  .object({
    nom: z.string().trim().min(1, 'Le nom est obligatoire').max(120).optional(),
    role: z.enum(ROLES).optional(),
    actif: z.boolean().optional(),
  })
  .refine((corps) => corps.nom !== undefined || corps.role !== undefined || corps.actif !== undefined, {
    message: 'Aucune modification demandée',
  });
export type MajUtilisateur = z.infer<typeof majUtilisateurSchema>;

/** Un compte vu par l'administration de l'equipe. */
export interface MembreEquipeDTO {
  id: string;
  email: string;
  nom: string;
  role: Role;
  /** Un compte desactive ne peut plus se connecter ; son historique reste attribue. */
  actif: boolean;
  derniereConnexionLe: string | null;
  creeLe: string;
  /** Leads dont ce compte est proprietaire. */
  nbLeads: number;
}

/** Un compte actif vu par tous : listes d'attribution, filtres par commercial. */
export interface MembreAnnuaireDTO {
  id: string;
  nom: string;
  role: Role;
}

/** Mot de passe provisoire : rendu une seule fois, jamais stocke en clair. */
export interface ReponseMotDePasseProvisoireDTO {
  motDePasseProvisoire: string;
}

export interface UtilisateurCreeDTO extends ReponseMotDePasseProvisoireDTO {
  utilisateur: MembreEquipeDTO;
}

/* -------------------------------------------------------------------------- */
/*                                   Erreurs                                   */
/* -------------------------------------------------------------------------- */

export interface ErreurApiDTO {
  erreur: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/* -------------------------------------------------------------------------- */
/*                              Jetons d'API                                   */
/* -------------------------------------------------------------------------- */

/**
 * Portees d'un jeton d'API : chaque route protegee en exige au moins une.
 * Les routes d'administration (statuts, jetons) n'en declarent aucune et
 * restent donc inaccessibles a un jeton, quelle que soit sa liste.
 */
export const PORTEES_JETON = [
  'leads:lire',
  'leads:ecrire',
  'imports:ecrire',
  'exports:lire',
  'dashboard:lire',
] as const;
export type PorteeJeton = (typeof PORTEES_JETON)[number];

export const creerJetonApiSchema = z.object({
  nom: z.string().min(1).max(80),
  portees: z.array(z.enum(PORTEES_JETON)).min(1),
  expireLe: dateIsoSchema.nullish(),
});
export type CreerJetonApi = z.infer<typeof creerJetonApiSchema>;

export interface JetonApiDTO {
  id: string;
  nom: string;
  /** Debut du secret (`lg_` + 8 caracteres), seule partie affichable. */
  prefixe: string;
  portees: PorteeJeton[];
  creeLe: string;
  expireLe: string | null;
  dernierUsageLe: string | null;
  revoqueLe: string | null;
  /** Ni revoque, ni expire : le jeton est accepte par l'API. */
  actif: boolean;
  /**
   * Compte au nom duquel le jeton agit, avec les droits de son role. La liste
   * montre les jetons de toute l'equipe : l'administration doit pouvoir
   * reconnaitre, puis revoquer, le jeton de n'importe quel membre.
   */
  creePar: { id: string; nom: string; email: string };
}

/** Le secret n'est renvoye qu'ici, a la creation : il n'est jamais stocke. */
export interface JetonApiCreeDTO {
  jeton: JetonApiDTO;
  secret: string;
}
